import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, Loader2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { getLegalPage } from "../services/legalService";

function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Standalone legal document page — one slug / one URL / one content body.
 */
export default function LegalPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!slug) {
        setError("Page not found");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const detail = await getLegalPage(slug);
        if (cancelled) return;
        if (!detail) {
          setPage(null);
          setError("This legal page is not available.");
        } else {
          setPage(detail);
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load this page. Please try again.");
          setPage(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const description = useMemo(() => {
    if (!page?.content_html) {
      return "Official legal information for OneSaaS Office Management System (OOMS).";
    }
    const text = stripHtml(page.content_html);
    return text.slice(0, 160) || page.title;
  }, [page]);

  const updatedLabel = page?.updated_at
    ? new Date(page.updated_at).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <>
      <SEO
        title={page?.title || "Legal"}
        description={description}
        canonicalUrl={slug ? `https://ooms.in/${slug}` : undefined}
      />

      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 rounded-[28px] border border-border bg-surface py-24 text-sm text-text-sub">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : error || !page ? (
          <div className="rounded-[28px] border border-border bg-surface p-10 text-center space-y-4">
            <FileText className="w-8 h-8 mx-auto text-text-sub" />
            <h1 className="font-heading font-black text-2xl text-foreground">
              Page unavailable
            </h1>
            <p className="text-sm text-text-sub">{error || "Not found."}</p>
            <Link
              to="/contact"
              className="inline-flex text-sm font-bold text-primary-600 hover:underline"
            >
              Contact support
            </Link>
          </div>
        ) : (
          <article className="rounded-[28px] border border-border bg-surface p-6 sm:p-10 lg:p-12 shadow-xl shadow-primary-600/5">
            <header className="mb-8 space-y-2 border-b border-border pb-6">
              <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-foreground">
                {page.title}
              </h1>
              {updatedLabel && (
                <p className="text-xs font-medium text-text-sub">
                  Last updated {updatedLabel}
                </p>
              )}
            </header>

            <div
              className="legal-content text-sm sm:text-[15px] leading-relaxed text-text-sub font-medium"
              dangerouslySetInnerHTML={{ __html: page.content_html || "" }}
            />
          </article>
        )}
      </section>

      <style>{`
        .legal-content h3 {
          margin: 1.5rem 0 0.75rem;
          font-family: inherit;
          font-size: 1.05rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: inherit;
        }
        .legal-content p {
          margin: 0 0 1rem;
        }
        .legal-content ul {
          margin: 0 0 1rem;
          padding-left: 1.25rem;
          list-style: disc;
        }
        .legal-content li {
          margin: 0.35rem 0;
        }
        .legal-content a {
          color: #4f46e5;
          font-weight: 700;
          text-decoration: underline;
        }
      `}</style>
    </>
  );
}
